import React, { useState, useRef, useEffect } from 'react';
import LogoMain from '../../assets/icons/logo-main.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { CustomContainer } from '../CustomMaterialComponents/CustomMaterial.component';
import { MainButton } from '../Buttons/Buttons.component';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    styled,
    Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import { MenuList } from '../../interfaces/menu-list.interface';
import Colors from '../../config/colors';
import worqRoutes from '../../config/routes';
import { secureNavigate } from '../../Utils/csrf';
import { FONT_USAGE } from '../../config/fonts';

export const HeaderButton = styled(Button)`
    font-family: ${FONT_USAGE.subheadings};
    text-transform: none;
    color: ${Colors.black};
    font-weight: 600;
    font-size: 1rem;
    padding: 8px 16px;
    border-radius: 8px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    /* Hover states */
    &:hover {
        background-color: ${Colors.purpleLighter};
        color: ${Colors.purple};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    /* Active state */
    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    /* Focus state for accessibility */
    &:focus-visible {
        outline: 2px solid ${Colors.purple};
        outline-offset: 2px;
        background-color: ${Colors.purpleLighter};
    }

    /* Mobile responsiveness */
    @media (max-width: 768px) {
        padding: 6px 12px;
        font-size: 0.875rem;
        min-height: 36px;
    }

    @media (max-width: 480px) {
        padding: 4px 8px;
        font-size: 0.8rem;
        min-height: 32px;
    }

    /* Disable hover effects on touch devices */
    @media (hover: none) {
        &:hover {
            background-color: transparent;
            color: ${Colors.black};
            transform: none;
            box-shadow: none;
        }
    }
`;

export const HeaderBox = styled(Box)`
    flex-grow: 1;
    display: flex;
    margin: 0 auto;
    align-items: center;
    justify-content: center;
    padding: 0.625rem;
    flex-wrap: nowrap;
    overflow: hidden;
`;

export const HeaderToolbar = styled(Toolbar)`
    height: 100%;
    padding: 0;
`;

export const HeaderAppBar = styled(AppBar)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: ${Colors.white};
`;

type Anchor = 'top' | 'left' | 'bottom' | 'right';

interface HeaderProps {
    menuList: MenuList[];
}

const Header = ({ menuList }: HeaderProps) => {
    const { t } = useTranslation();
    const [state, setState] = useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });
    const drawerRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    // Focus management for drawer
    useEffect(() => {
        if (state.right && drawerRef.current) {
            const firstFocusableElement = drawerRef.current.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement;
            if (firstFocusableElement) {
                firstFocusableElement.focus();
            }
        } else if (!state.right && menuButtonRef.current) {
            menuButtonRef.current.focus();
        }
    }, [state.right]);

    const list = (anchor: Anchor) => (
        <Box
            ref={drawerRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            role="presentation"
        >
            {/* Mobile Navigation Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px 16px',
                    borderBottom: `1px solid ${Colors.purpleLighter}`,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: FONT_USAGE.headings,
                        fontWeight: 700,
                        color: Colors.purple,
                        fontSize: '1.25rem',
                    }}
                >
                    Menu
                </Typography>
                <IconButton
                    onClick={toggleDrawer(anchor, false)}
                    sx={{
                        color: Colors.purple,
                        '&:hover': {
                            backgroundColor: Colors.purpleLighter,
                        },
                    }}
                    aria-label="Close navigation menu"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Navigation Items */}
            <List
                sx={{
                    padding: '16px',
                    flex: 1,
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 200px)',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: Colors.purpleLighter,
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: Colors.purple,
                        borderRadius: '3px',
                        '&:hover': {
                            background: Colors.purpleDark,
                        },
                    },
                }}
            >
                {menuList.map((menuItem, index) => (
                    <ListItem
                        key={menuItem.key + index}
                        sx={{
                            padding: '0',
                            marginBottom: '12px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                        }}
                    >
                        <ListItemButton
                            href={menuItem.route}
                            onClick={toggleDrawer(anchor, false)}
                            tabIndex={0}
                            sx={{
                                padding: '20px 24px',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease',
                                border: '1px solid transparent',
                                '&:hover': {
                                    backgroundColor: Colors.purpleLighter,
                                    borderColor: Colors.purple,
                                    transform: 'translateX(8px)',
                                },
                                '&:active': {
                                    transform: 'translateX(4px)',
                                    backgroundColor: Colors.purpleLight,
                                },
                                '&:focus-visible': {
                                    outline: `2px solid ${Colors.purple}`,
                                    outlineOffset: '2px',
                                    backgroundColor: Colors.purpleLighter,
                                },
                                '&:focus': {
                                    backgroundColor: Colors.purpleLighter,
                                },
                            }}
                        >
                            <ListItemText
                                primary={t(menuItem.key)}
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontFamily: FONT_USAGE.subheadings,
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        color: Colors.black,
                                    },
                                }}
                            />
                            <KeyboardArrowDownIcon
                                sx={{
                                    color: Colors.purple,
                                    transform: 'rotate(-90deg)',
                                    fontSize: '1.25rem',
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            {/* Mobile Navigation Footer */}
            <Box
                sx={{
                    padding: '20px 24px',
                    borderTop: `1px solid ${Colors.purpleLighter}`,
                    backgroundColor: Colors.purpleLighter,
                }}
            >
                <MainButton
                    onClick={() => {
                        secureNavigate('https://app.worq.com.au');
                        toggleDrawer(anchor, false);
                    }}
                    aria-label="Login to App"
                    sx={{
                        width: '100%',
                        height: '40px',
                        fontSize: '1rem',
                        fontWeight: 600,
                    }}
                >
                    Login to App
                </MainButton>
            </Box>
        </Box>
    );

    const toggleDrawer =
        (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
            if (
                event &&
                event.type === 'keydown' &&
                ((event as React.KeyboardEvent).key === 'Tab' ||
                    (event as React.KeyboardEvent).key === 'Shift')
            ) {
                return;
            }

            setState({ ...state, [anchor]: open });
        };

    return (
        <HeaderAppBar sx={{ height: { xs: '70px', md: '80px' } }}>
            <CustomContainer>
                <HeaderToolbar
                    disableGutters
                    sx={{
                        justifyContent: { xs: 'space-between', md: 'space-between' },
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    <Button
                        href={worqRoutes.home}
                        sx={{
                            padding: '6px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: Colors.purpleLighter,
                                transform: 'scale(1.02)',
                            },
                            '&:active': {
                                transform: 'scale(0.98)',
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${Colors.purple}`,
                                outlineOffset: '2px',
                            },
                        }}
                        aria-label="Go to home page"
                    >
                        <Box
                            component="img"
                            src={LogoMain}
                            alt="WORQ - Compliance and regulation platform logo"
                            sx={{
                                width: { xs: '120px', lg: '160px' },
                            }}
                        />
                    </Button>
                    <HeaderBox
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'row',
                            flexWrap: 'nowrap',
                            gap: { md: 1, lg: 2 },
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: '1 1 auto',
                            minWidth: 0,
                        }}
                    >
                        {menuList.map(menuItem => (
                            <HeaderButton
                                key={menuItem.key}
                                endIcon={<KeyboardArrowDownIcon />}
                                href={menuItem.route}
                                sx={{
                                    fontSize: { xs: '15px', sm: '16px', lg: '18px' },
                                    paddingLeft: { xs: '10px', lg: '15px' },
                                    paddingRight: { xs: '10px', lg: '15px' },
                                    whiteSpace: 'nowrap',
                                    minWidth: 'fit-content',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {t(menuItem.key)}
                            </HeaderButton>
                        ))}
                    </HeaderBox>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
                        <MainButton
                            onClick={() => secureNavigate('https://app.worq.com.au')}
                            aria-label="Login"
                            sx={{
                                height: '40px',
                                minWidth: '120px',
                                fontSize: { xs: '14px', sm: '16px', lg: '18px' },
                            }}
                        >
                            Login
                        </MainButton>
                    </Box>
                    <IconButton
                        ref={menuButtonRef}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            padding: '8px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: Colors.purpleLighter,
                                transform: 'scale(1.05)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${Colors.purple}`,
                                outlineOffset: '2px',
                            },
                        }}
                        onClick={toggleDrawer('right', true)}
                        aria-label="Open navigation menu"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Drawer
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                width: { xs: '100%', sm: '320px' },
                                height: '100%',
                                backgroundColor: Colors.white,
                                borderLeft: `1px solid ${Colors.purpleLighter}`,
                                boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.15)',
                                borderRadius: { xs: '0', sm: '16px 0 0 16px' },
                                overflow: 'hidden',
                            },
                            '& .MuiBackdrop-root': {
                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            },
                        }}
                        anchor={'right'}
                        open={state['right']}
                        onClose={toggleDrawer('right', false)}
                        ModalProps={{
                            keepMounted: true, // Better mobile performance
                            disableRestoreFocus: false,
                            disableAutoFocus: false,
                            disableEnforceFocus: false,
                        }}
                        transitionDuration={300}
                        variant="temporary"
                        disableScrollLock={false}
                    >
                        {list('right')}
                    </Drawer>
                </HeaderToolbar>
            </CustomContainer>
        </HeaderAppBar>
    );
};

export default Header;
